import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Shield,
  Phone,
  Key,
  CheckCircle,
  XCircle,
  MessageCircle,
  Lock,
  Mail
} from 'lucide-react';
import { PhoneVerification } from '../../components/verification/PhoneVerification';
import toast from '../../utils/toast';
import { EmailVerification } from '../../components/verification/EmailVerification';
import { PasswordChange } from '../../components/settings/PasswordChange';
import { formatDistanceToNow } from 'date-fns';
// import SettingsSkeleton from '../../components/settings/SettingsSkeleton';

const SecuritySettings = () => {
  const { user, updateUser, isLoading } = useAuth();
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userPhone, setUserPhone] = useState(user?.phone || '');
  const [userEmail, setUserEmail] = useState(user?.email || '');
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const lastChanged = user?.password_changed_at
    ? formatDistanceToNow(new Date(user.password_changed_at), { addSuffix: true })
    : 'Never';
    
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    // Call API to change password
    toast.success('Password changed successfully');
    setShowChangePassword(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const securityItems = [
    {
      title: 'Phone Verification',
      description: 'Verify your phone number for security and updates',
      icon: Phone,
      status: user?.phone_verified ? 'verified' : 'not-verified',
      action: () => setShowPhoneVerification(true),
      buttonText: user?.phone_verified ? 'Re-verify' : 'Verify Now',
      buttonVariant: user?.phone_verified ? 'outline' : 'primary'
    },
    {
      title: 'Email Verification',
      description: 'Verify your email address for account security',
      icon: Mail,
      status: user?.email_verified ? 'verified' : 'not-verified',
      action: () => setShowEmailVerification(true),
      buttonText: user?.email_verified ? 'Re-verify' : 'Verify Now',
    },
    {
      title: 'Two-Factor Authentication',
      description: 'Add an extra layer of security',
      icon: Shield,
      status: 'not-configured',
      action: () => toast.info('2FA coming soon'),
      buttonText: 'Enable',
      buttonVariant: 'outline'
    },
    {
      title: 'Active Sessions',
      description: '2 devices currently active',
      icon: Lock,
      status: 'active',
      action: () => toast.info('Session management coming soon'),
      buttonText: 'Manage',
      buttonVariant: 'outline'
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
      case 'configured':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'active':
        return <div className="h-2 w-2 rounded-full bg-green-500"></div>;
      default:
        return <XCircle className="h-5 w-5 text-gray-300" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'verified': return 'Verified';
      case 'configured': return 'Configured';
      case 'active': return 'Active';
      default: return 'Not set up';
    }
  };

  // if (isLoading) {
  //   return <SettingsSkeleton />; // Full page skeleton
  // }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900">Account & Security</h2>
        <p className="text-gray-600 mt-1">
          Manage your security settings and verification methods
        </p>
      </div>

      {/* Always visible password section */}
      <div className="mb-8">
        <PasswordChange />
      </div>


      {/* Security Items List */}
      <div className="space-y-4">
        {securityItems.map((item, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <div className="flex items-start space-x-4">
              <div className="p-2 bg-primary-50 rounded-lg">
                <item.icon className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{item.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                <div className="flex items-center mt-2">
                  {getStatusIcon(item.status)}
                  <span className="text-xs text-gray-600 ml-2">
                    {getStatusText(item.status)}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={item.action}
              className={`
                px-4 py-2 text-sm font-medium rounded-lg transition-colors
                ${item.buttonVariant === 'primary'
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                }
              `}
            >
              {item.buttonText}
            </button>
          </div>
        ))}
      </div>

      {/* Phone Verification Modal */}
      {showPhoneVerification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <PhoneVerification
            currentPhone={userPhone}
            onVerified={(verifiedPhone) => {
              updateUser({ phone: verifiedPhone, phone_verified: true });
              setUserPhone(verifiedPhone);
              setShowPhoneVerification(false);
            }}
            onCancel={() => setShowPhoneVerification(false)}
          />
        </div>
      )}

      {/* Email Verification Modal */}
      {showEmailVerification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <EmailVerification
            currentEmail={userEmail}
            onVerified={(verifiedEmail) => {
              updateUser({ email: verifiedEmail, email_verified: true });
              setUserEmail(verifiedEmail);
              setShowEmailVerification(false);
            }}
            onCancel={() => setShowEmailVerification(false)}
          />
        </div>
      )}

      {/* Security Tips */}
      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-medium text-blue-800 mb-2">Security Tips</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Use a strong, unique password</li>
          <li>• Verify your phone number for account recovery</li>
          <li>• Enable two-factor authentication for extra security</li>
          <li>• Regularly review active sessions</li>
        </ul>
      </div>
    </div>
  );
};

export default SecuritySettings;